import { SOCIAL_CHANNEL_ID, socialChannelIdsOnPieces } from '@/app/common/brief-pieces/piece-rules';
import { Piece, Placement, SocialPlacementChannel } from '@/app/common/general-types/briefs.dto';

type ResultsBrief = {
  shoutBrief?: {
    socialPlatform?: number | null;
    crossPlatforms?: (string | number)[];
    suggestedHastags?: string;
    callToActionLink?: string;
  } | null;
  pieces?: Piece[] | null;
};

export type ResultsPieceScope = {
  /** The approved video's piece. Omit on a brief with no piece list. */
  pieceLabel?: string | null;
  /** Placements still owed on this hire. Omit before hire. */
  owed?: readonly (
    | { pieceLabel: string; kind: 'ugc' }
    | { pieceLabel: string; kind: 'social'; channel: SocialPlacementChannel }
  )[] | null;
};

/** A brief is UGC only when it has no shout brief and no social placement. */
export function briefIsUgcOnly(brief: ResultsBrief | null | undefined): boolean {
  if (brief?.shoutBrief) {
    return false;
  }
  return socialChannelIdsOnPieces(brief?.pieces ?? []).length === 0;
}

export function resultsPrimaryChannel(
  brief: ResultsBrief | null | undefined,
  scope?: ResultsPieceScope,
): number | undefined {
  if (brief?.pieces?.length) {
    return channelsForPiece(brief, scope)[0];
  }
  const platform = brief?.shoutBrief?.socialPlatform;
  if (platform != null) {
    return Number(platform);
  }
  return undefined;
}

export function resultsCrossPlatforms(
  brief: ResultsBrief | null | undefined,
  scope?: ResultsPieceScope,
): (string | number)[] {
  if (brief?.pieces?.length) {
    return channelsForPiece(brief, scope);
  }
  const cross = brief?.shoutBrief?.crossPlatforms;
  if (cross?.length) {
    return cross;
  }
  if (brief?.shoutBrief) {
    return [];
  }
  return [];
}

/** Social channels the open video is posted to. A piece list does not share channels across videos. */
function channelsForPiece(brief: ResultsBrief, scope?: ResultsPieceScope): number[] {
  const pieces = brief.pieces ?? [];
  const label = scope?.pieceLabel?.trim();
  const piece = label
    ? pieces.find((item) => item.label.trim() === label)
    : undefined;
  const source = piece ? [piece] : label ? [] : pieces;
  let ids = socialChannelIdsOnPieces(source);
  const owed = scope?.owed;
  if (!piece || !owed?.length) {
    return ids;
  }
  const allowed = new Set<number>();
  for (const placement of owed) {
    if (placement.kind !== 'social' || placement.pieceLabel.trim() !== piece.label.trim()) {
      continue;
    }
    allowed.add(SOCIAL_CHANNEL_ID[placement.channel]);
  }
  ids = ids.filter((id) => allowed.has(id));
  return ids;
}

export function resultsHashtags(
  brief: ResultsBrief | null | undefined,
  pieceLabel?: string | null,
): string | undefined {
  return brief?.shoutBrief?.suggestedHastags ?? socialPlacement(brief, pieceLabel)?.hashtags;
}

export function resultsCallToAction(
  brief: ResultsBrief | null | undefined,
  pieceLabel?: string | null,
): string | undefined {
  return brief?.shoutBrief?.callToActionLink ?? socialPlacement(brief, pieceLabel)?.callToActionLink;
}

function socialPlacement(
  brief: ResultsBrief | null | undefined,
  pieceLabel?: string | null,
): Extract<Placement, { kind: 'social' }> | undefined {
  const pieces = brief?.pieces ?? [];
  const label = pieceLabel?.trim();
  const piece = (label ? pieces.find((item) => item.label === label) : undefined)
    ?? pieces.find((item) => item.placements.some((placement) => placement.kind === 'social'));
  return piece?.placements.find(
    (placement): placement is Extract<Placement, { kind: 'social' }> => placement.kind === 'social',
  );
}
